import * as React from 'react';
import { memo, useCallback } from 'react';

import styled, { keyframes } from 'styled-components';

import { useToasts } from 'core/hooks';

import { IAppGlobalToast } from 'core';
import { ToastComponent } from './Toast';

const enterAnimation = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const leaveAnimation = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

export const Toast = memo(
  ({
    id,
    data: { type, title, text, actionLabel, onAction },
  }: IAppGlobalToast) => {
    const { removeToast } = useToasts();
    const onAnimationEnd = useCallback(() => {
      removeToast(id);
    }, [id, removeToast]);
    const hasAction = Boolean(actionLabel && onAction);

    return (
      <Wrapper
        $hasAction={hasAction}
        onClick={() => removeToast(id)}
        onAnimationEnd={(e) =>
          Boolean(e.animationName === leaveAnimation.getName()) &&
          onAnimationEnd()
        }
      >
        <ToastComponentStyled
          type={type}
          title={title}
          text={text}
          actionLabel={actionLabel}
          onAction={
            onAction
              ? () => {
                  onAction();
                  removeToast(id);
                }
              : undefined
          }
        />
      </Wrapper>
    );
  },
);

const Wrapper = styled.div<{ $hasAction?: boolean }>`
  width: 100%;

  &:not(:first-of-type) {
    margin-top: 20px;
  }

  cursor: pointer;
  pointer-events: auto;
  /* A toast carrying an action lingers ~10s instead of 4: an undo the user
     cannot reach in time is not an undo (spec 0030 r2). */
  animation: ${leaveAnimation} 0.6s ease-in
      ${({ $hasAction }) => ($hasAction ? '9.4s' : '3.4s')},
    ${enterAnimation} 0.4s ease-out forwards;
`;

const ToastComponentStyled = styled(ToastComponent)`
  width: 100%;
`;
